import { useCallback, useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'

import type { Department } from '@/api/types'
import { Badge } from '@/components/ui/Badge'
import { KpiCard } from '@/components/ui/KpiCard'
import { Notice } from '@/components/ui/Notice'
import { Panel } from '@/components/ui/Panel'
import { formatMoney, formatPercent } from '@/lib/format'

import { useDrillDown } from '../drilldown/DrillDownContext'
import { labelWithAsOf, monthNumber, monthRange, periodStartLabel, quarterEndLabel } from '../lib/calendar'
import {
  isPaceApplicable,
  isPeriodComplete,
  paceStatus,
  paceWords,
  pctOfBudget,
  projectedTotal,
  varianceStatus,
  varianceWords,
} from '../lib/pace'
import { actualLabel, periodShort } from '../lib/periodText'
import { useRunwayModel } from '../RunwayContext'
import { AccountTable } from './AccountTable'
import { type BulletRowData, BulletRows } from './BulletRows'
import layout from './Layout.module.css'
import { type NotableRowData, NotableTable } from './NotableTable'
import { PacePanel } from './PacePanel'
import { PageHead } from './PageHead'
import { TrendChart } from './TrendChart'
import { VarianceTag } from './VarianceTag'
import { VendorRows } from './VendorRows'

function categoryActionLabel(row: BulletRowData): string {
  return `${row.name}: view transactions`
}

function DepartmentView({ code, dept }: { code: string; dept: Department }) {
  const { runway, calendar, pacePct, deptName } = useRunwayModel()
  const { openDrillDown } = useDrillDown()
  const { period } = runway
  const quarterShort = periodShort(period)
  const label = period.label
  const months = monthRange(calendar)
  const complete = isPeriodComplete(runway)
  const paceApplies = isPaceApplicable(runway)
  const hasBudget = dept.quarter_budget > 0
  const used = pctOfBudget(dept.qtd_actual, dept.quarter_budget)
  const status = paceStatus(used, pacePct)
  const projected =
    hasBudget && paceApplies
      ? projectedTotal(dept.qtd_actual, runway.days_elapsed_in_quarter, runway.days_total_in_quarter)
      : null
  const projectedVariance = projected === null ? null : projected - dept.quarter_budget
  // Variance = actual − budget (positive = over); left to spend = budget − actual.
  const variance = dept.qtd_actual - dept.quarter_budget
  const remaining = dept.quarter_budget - dept.qtd_actual
  const codes = useMemo(() => [code], [code])

  let badge
  if (used === null) badge = <Badge tone="neutral">No {label} budget on file</Badge>
  else if (complete) {
    const words = varianceWords(variance)
    badge = (
      <Badge tone={variance <= 0 ? 'good' : 'bad'}>
        {words === 'on budget' ? 'Final: on budget' : `Final: ${formatMoney(Math.abs(variance))} ${words} budget`}
      </Badge>
    )
  } else badge = <Badge tone={status === 'good' ? 'good' : 'bad'}>{paceWords(used, pacePct)}</Badge>

  const categoryRows = useMemo<BulletRowData[]>(
    () =>
      dept.categories.map((c) => ({
        key: c.category,
        name: c.category,
        actual: c.actual,
        budget: c.budget,
        status: varianceStatus(c.variance),
        figures: (
          <>
            {formatMoney(c.actual, { compact: true })}{' '}
            <span className="text-faint">/ {formatMoney(c.budget, { compact: true })}</span> ·{' '}
            <VarianceTag variance={c.variance} />
          </>
        ),
      })),
    [dept.categories],
  )

  const drillAll = useCallback(
    () => openDrillDown({ dept: code, filter: { type: 'all' }, label: `All ${months} transactions` }),
    [openDrillDown, code, months],
  )
  const drillMonth = useCallback(
    (month: string) =>
      openDrillDown({
        dept: code,
        filter: { type: 'month', value: monthNumber(calendar, month) ?? 0 },
        label: `${month} ${calendar.year} transactions`,
      }),
    [openDrillDown, code, calendar],
  )
  const drillVendor = useCallback(
    (who: string) => openDrillDown({ dept: code, filter: { type: 'vendor', value: who }, label: who }),
    [openDrillDown, code],
  )
  const drillAccount = useCallback(
    (account: string) => {
      const row = dept.accounts.find((a) => a.account === account)
      openDrillDown({ dept: code, filter: { type: 'acct', value: account }, label: `${account} — ${row?.title ?? ''}` })
    },
    [openDrillDown, code, dept.accounts],
  )
  const drillCategory = useCallback(
    (category: string) => openDrillDown({ dept: code, filter: { type: 'cat', value: category }, label: category }),
    [openDrillDown, code],
  )
  const drillNotable = useCallback(
    (row: NotableRowData) =>
      openDrillDown({ dept: code, filter: { type: 'acct', value: row.acct }, label: `${row.acct} — ${row.title}` }),
    [openDrillDown, code],
  )
  const notableRows = useMemo<NotableRowData[]>(
    () => dept.notable.map((n) => ({ ...n, dept: code })),
    [dept.notable, code],
  )

  return (
    <div className={layout.view} key={code}>
      <PageHead
        title={dept.name}
        sub={`Expenses vs. budget · ${label} (${months}) · as of ${runway.as_of}`}
        badge={badge}
      />
      {!hasBudget ? (
        <Notice tone="warn">
          <b>No budget on file.</b> {dept.name} has no line items in the {label} budget — every dollar below is
          unbudgeted spend.
        </Notice>
      ) : null}
      {dept.grouping === 'sage_hierarchy' ? (
        <Notice>
          <b>From Sage&rsquo;s hierarchy:</b> this tile rolls up {dept.source_depts.length} Sage departments (
          {dept.source_depts.join(', ')}). A department Sage adds under it is counted here on the next sync.
        </Notice>
      ) : dept.note ? (
        <Notice>{dept.note}</Notice>
      ) : null}

      <div className={layout.kpiRow}>
        <KpiCard
          label={actualLabel(period)}
          value={formatMoney(dept.qtd_actual)}
          sub={`${periodStartLabel(calendar)} – ${runway.as_of}`}
          onActivate={drillAll}
          activateLabel={`QTD actual ${formatMoney(dept.qtd_actual)}: view every transaction`}
        />
        <KpiCard label={`${label} Budget`} value={formatMoney(dept.quarter_budget)} sub={`Full ${months} plan`} />
        {complete ? (
          <KpiCard
            label={`${quarterShort} Final vs. Budget`}
            value={hasBudget ? formatMoney(Math.abs(variance)) : '—'}
            tone={hasBudget ? (variance <= 0 ? 'good' : 'bad') : null}
            sub={hasBudget ? `${varianceWords(variance)} budget, final` : 'no budget on file'}
          />
        ) : (
          <KpiCard
            label={paceApplies ? `Projected ${quarterShort} Total` : 'Projected Total'}
            value={projected === null ? '—' : formatMoney(projected)}
            tone={projectedVariance === null ? null : projectedVariance <= 0 ? 'good' : 'bad'}
            sub={
              projectedVariance === null
                ? hasBudget
                  ? 'needs adjoining months'
                  : 'no budget to project against'
                : varianceWords(projectedVariance) === 'on budget'
                  ? 'on budget at this pace'
                  : `${formatMoney(Math.abs(projectedVariance))} ${varianceWords(projectedVariance)} budget at this pace`
            }
          />
        )}
        {!hasBudget ? (
          <KpiCard label="Left to Spend" value="—" sub="no budget on file" />
        ) : complete ? (
          <KpiCard
            label="Budget Used"
            value={formatPercent(used)}
            tone={variance <= 0 ? 'good' : 'bad'}
            sub={`of the full ${months} plan`}
          />
        ) : remaining < 0 ? (
          <KpiCard label="Over Budget By" value={formatMoney(-remaining)} tone="bad" sub="spent beyond the full plan" />
        ) : (
          <KpiCard
            label="Left to Spend"
            value={formatMoney(remaining)}
            sub={complete ? 'unspent at period end' : `Runway through ${quarterEndLabel(calendar)}`}
          />
        )}
      </div>

      <PacePanel
        title={`Spending pace — ${labelWithAsOf(runway)}`}
        codes={codes}
        budget={dept.quarter_budget}
        hint="hover a point for the running total"
      />

      <div className={layout.grid2}>
        <Panel title="By month" aside="Select a solid bar for detail">
          <TrendChart monthly={dept.monthly} year={calendar.year} onSelectMonth={drillMonth} />
        </Panel>
        <Panel title="Top vendors" aside={`By $ in ${label} · click for detail`}>
          <VendorRows payees={dept.top_payees} onActivate={drillVendor} />
        </Panel>
      </div>

      <div className={layout.grid2}>
        <Panel title="Line items" aside="Sorted by variance, largest first · click a row for detail">
          <AccountTable
            accounts={dept.accounts}
            pacePct={pacePct}
            quarterShort={quarterShort}
            onActivate={drillAccount}
          />
        </Panel>
        <Panel title="By category" aside="Ranked by |variance| · click a row for detail">
          <BulletRows rows={categoryRows} onActivate={drillCategory} actionLabel={categoryActionLabel} />
        </Panel>
      </div>

      <Panel
        title="Notable transactions"
        aside={`Largest single vendor transactions in ${label} · credits in parentheses`}
      >
        <NotableTable rows={notableRows} showDept={false} deptName={deptName} onActivate={drillNotable} />
      </Panel>
    </div>
  )
}

export function DepartmentPage() {
  const { code = '' } = useParams()
  const { runway } = useRunwayModel()
  const dept = runway.departments[code]
  if (!dept) return <Navigate to="/" replace />
  return <DepartmentView key={code} code={code} dept={dept} />
}
